import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PaintRoller, Wall, SquaresFour, Buildings, CheckCircle, Star, Trophy, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1582289545106-efecf907f21e?q=85&w=1920&auto=format&fit=crop";

const serviceIcons = {
  "platrerie-isolation": Wall,
  "peinture-decoration": PaintRoller,
  "revetements": SquaresFour,
  "ravalement-facades": Buildings,
};

const stats = [
  { icon: Trophy, value: "25+", label: "Années d'expérience" },
  { icon: Users, value: "1000+", label: "Clients satisfaits" },
  { icon: CheckCircle, value: "1000+", label: "Projets réalisés" },
  { icon: Star, value: "4.6/5", label: "Note Google" },
];

const testimonials = [
  {
    name: "Janine",
    location: "Mâcon",
    text: "Travail impeccable ! Je suis ravie du résultat, très professionnel, je recommande :)",
    rating: 5,
  },
  {
    name: "Myriam CRUEL",
    location: "Mâcon",
    text: "Personnes très professionnelles, travail bien fait et laisse un chantier propre en partant.",
    rating: 5,
  },
  {
    name: "Souliman Chkr",
    location: "Mâcon",
    text: "Les peintures sont de très bonnes qualités et le travail est super propre. Je remercie l'équipe de Qualidéco !",
    rating: 5,
  },
];

const trustedLogos = [
  { name: "Mâcon Habitat", src: "https://customer-assets.emergentagent.com/job_8daf730c-66f1-48f9-9ec4-f1965e82cf39/artifacts/wy366r2v_Logo%20Macon%20Habitat.png" },
  { name: "OPAC Saône & Loire", src: "https://customer-assets.emergentagent.com/job_8daf730c-66f1-48f9-9ec4-f1965e82cf39/artifacts/sln07dg5_Logo%20Opac.png" },
  { name: "MBA", src: "https://customer-assets.emergentagent.com/job_8daf730c-66f1-48f9-9ec4-f1965e82cf39/artifacts/rfk62b36_Logo%20MBA.png" },
  { name: "Ville de Mâcon", src: "https://customer-assets.emergentagent.com/job_8daf730c-66f1-48f9-9ec4-f1965e82cf39/artifacts/7l676sb5_Logo%20ville%20de%20macon.png" },
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, portfolioRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/portfolio`),
        ]);
        setServices(servicesRes.data);
        setPortfolio(portfolioRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#F8F9FA]" data-testid="hero-section">
        <div className="absolute inset-0 bg-pattern opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="flex items-center gap-3">
                <span className="bg-blue-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-sm">
                  QUALIBAT
                </span>
                <span className="bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-sm">
                  RGE
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333333] leading-tight">
                L'Art de la{" "}
                <span className="text-[#E74C3C]">Finition</span>{" "}
                depuis 2002
              </h1>
              
              <p className="text-lg text-[#64748B] max-w-lg">
                Experts en plâtrerie, peinture, revêtements et ravalement de façades. 
                Qualidéco transforme vos espaces avec passion et savoir-faire en Saône-et-Loire. 
                Certifié RGE et Qualibat.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-8 py-6 rounded-sm text-base transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    data-testid="hero-cta-devis"
                  >
                    Demander un Devis Gratuit
                    <ArrowRight className="ml-2" size={20} weight="bold" />
                  </Button>
                </Link>
                <Link to="/realisations">
                  <Button
                    variant="outline"
                    className="border-2 border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white font-bold uppercase tracking-wide px-8 py-6 rounded-sm text-base transition-all"
                    data-testid="hero-cta-portfolio"
                  >
                    Voir nos Réalisations
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-sm overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Travaux de peinture professionnels"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-sm shadow-xl">
                <p className="text-5xl font-bold text-[#E74C3C] font-['Barlow_Condensed']">25+</p>
                <p className="text-sm text-[#64748B] font-medium">Années d'expérience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#333333] py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <Icon size={40} weight="duotone" className="mx-auto mb-3 text-[#1B93A4]" />
                  <p className="text-4xl font-bold text-white font-['Barlow_Condensed']">{stat.value}</p>
                  <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trusted By Section - Infinite Logo Marquee */}
      <section className="py-16 bg-white overflow-hidden" data-testid="trusted-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Références
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#333333] mt-3">
              Ils Nous Font Confiance
            </h2>
          </motion.div>
        </div>
        
        {/* Infinite Marquee */}
        <div className="relative">
          <div className="flex animate-marquee">
            {[...trustedLogos, ...trustedLogos, ...trustedLogos, ...trustedLogos].map((logo, index) => (
              <div
                key={`${logo.name}-${index}`}
                className="flex-shrink-0 mx-8 flex items-center justify-center"
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="h-20 w-auto object-contain hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Nos Expertises
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#333333] mt-3">
              Des Services Complets
            </h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">
              De la plâtrerie au ravalement de façades, nous maîtrisons tous les corps de métiers 
              pour transformer vos espaces.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = serviceIcons[service.id] || PaintRoller;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white border border-gray-100 hover:border-[#1B93A4] rounded-sm overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="bg-[#1B93A4] p-3 rounded-sm">
                        <Icon size={24} weight="fill" className="text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-[#64748B] mb-4 line-clamp-2">{service.description}</p>
                    <ul className="space-y-2 mb-6">
                      {service.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#333333]">
                          <CheckCircle size={16} weight="fill" className="text-[#1B93A4]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/services#${service.id}`}
                      className="inline-flex items-center gap-2 text-[#E74C3C] font-medium hover:gap-3 transition-all"
                    >
                      En savoir plus
                      <ArrowRight size={16} weight="bold" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-2 border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm"
                data-testid="view-all-services-btn"
              >
                Tous nos Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-24 bg-[#F8F9FA]" data-testid="portfolio-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Portfolio
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#333333] mt-3">
              Nos Dernières Réalisations
            </h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">
              Découvrez quelques-uns de nos projets récents et laissez-vous inspirer pour votre prochain chantier.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {portfolio.map((item, index) => {
              const mainImage = item.images ? item.images[0] : (item.after_image || item.image);
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative overflow-hidden rounded-sm shadow-lg"
                  data-testid={`portfolio-preview-${item.id}`}
                >
                  <img
                    src={mainImage}
                    alt={item.title}
                    className="w-full h-72 object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-[#1B93A4] text-sm font-medium mb-1">{item.location}</p>
                    <h3 className="text-white text-lg font-bold">{item.title}</h3>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/realisations">
              <Button
                className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm"
                data-testid="view-all-portfolio-btn"
              >
                Voir Toutes les Réalisations
                <ArrowRight className="ml-2" size={20} weight="bold" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Témoignages
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#333333] mt-3">
              Ils Nous Font Confiance
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F8F9FA] p-8 rounded-sm"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} weight="fill" className="text-yellow-500" />
                  ))}
                </div>
                <p className="text-[#333333] mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-[#333333]">{testimonial.name}</p>
                  <p className="text-sm text-[#64748B]">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#333333] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-pattern" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Prêt à Transformer Votre Espace ?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Contactez-nous dès maintenant pour un devis gratuit et personnalisé. 
              Notre équipe est à votre écoute pour concrétiser vos projets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-8 py-6 rounded-sm text-base"
                  data-testid="final-cta-devis"
                >
                  Demander un Devis Gratuit
                </Button>
              </Link>
              <a href="tel:0385392938">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#333333] font-bold uppercase tracking-wide px-8 py-6 rounded-sm text-base"
                  data-testid="final-cta-phone"
                >
                  Appelez-nous
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
